import { ApolloQueryResult, gql, useApolloClient } from '@apollo/client'
import React from 'react'

import { getStoreViews } from '@emico/storeviews'

import withSentry from '../../utils/withSentry'

const storeViewsCategories = `
query GetCategoryMeta($id: Int!){
    ${getStoreViews()
        .map(
            ({ code }) => `
        ${code}: category(id: $id, store: "${code}"){
                urlKey
                breadcrumbs {
                    categoryLevel
                    categoryUrlKey
                }
            }
        `,
        )
        .join('')}}`

const query = gql(storeViewsCategories)

export default query
export interface GetCategoryMetaQueryVariables {
    id: number
}

export interface CategoryMeta {
    breadcrumbs: [
        {
            categoryLevel: number
            categoryUrlKey: string
            categoryId: number | null
        },
    ]
    urlKey: string
}

export type GetCategoryMetaResult = Record<string, CategoryMeta>

export const useCategoryMeta = (storeview: string, id: number) => {
    const apolloClient = useApolloClient()
    const [result, setResult] = React.useState<
        ApolloQueryResult<GetCategoryMetaResult> | undefined
    >()

    React.useEffect(() => {
        apolloClient
            .query<GetCategoryMetaResult, GetCategoryMetaQueryVariables>({
                query,
                variables: {
                    id,
                },
                fetchPolicy: 'no-cache',
                // The Store header sets the storeview for which the query is run.
                // The storeview determines the locale of all content fetched.
                // By default, this is set for all queries upon initialisation of the apolloClient
                // But because meta data has to be fetched for other locales than the default,
                // we need to set it here, so we can fetch content in differnt locale than the default
                context: {
                    headers: {
                        Store: storeview,
                        'x-cache': 1,
                    },
                    queryDeduplication: false,
                },
            })
            .then((result) => {
                setResult(result)
            })
            .catch((err) => {
                withSentry((Sentry) => {
                    Sentry.withScope((scope: SentryScope) => {
                        scope.setExtra('categoryId', id)
                        scope.setExtra('storeview', storeview)
                        Sentry.captureException(err)
                    })
                })
                setResult(undefined)
            })
    }, [apolloClient, id, storeview])
    return result
}
